import { BRONZE_600 } from "@shared/brand-colors";

export const ADMIN_STYLES = {
  pageWrapper: "flex min-h-screen bg-background",
  main: "flex-1 min-w-0 p-4 sm:p-5 lg:p-6 pt-16 lg:pt-6 pb-24 lg:pb-6",
  header: {
    wrapper: "mb-5 sm:mb-6",
    title: "text-2xl sm:text-3xl font-serif font-semibold tracking-tight text-foreground",
    subtitle: "text-sm font-sans font-normal tracking-tight text-muted-foreground mt-1.5",
  },
  section: {
    wrapper: "mb-5 sm:mb-6",
    title: "text-lg sm:text-xl font-serif font-semibold tracking-tight text-foreground mb-3",
  },
  card: {
    wrapper: "hover:shadow-md transition-shadow",
    header: "flex flex-row items-center justify-between space-y-0 pb-2",
    title: "text-[13px] font-sans font-medium tracking-tight text-muted-foreground",
    value: "text-2xl sm:text-3xl font-serif font-semibold tracking-tight",
    subtitle: "text-[12px] font-sans font-normal tracking-tight text-muted-foreground",
  },
  grid: {
    stats: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5 sm:mb-6",
    twoCol: "grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6",
    threeCol: "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4",
  },
  table: {
    wrapper: "overflow-x-auto",
    header: "text-left text-xs font-sans font-medium text-muted-foreground uppercase tracking-wide",
    cell: "py-2.5 px-2 sm:px-3 text-[13px]",
    row: "border-b hover:bg-muted/50 transition-colors",
  },
  skeleton: {
    card: "h-24 sm:h-28 rounded-lg animate-pulse bg-muted",
    text: "h-3.5 rounded animate-pulse bg-muted",
    textLg: "h-5 rounded animate-pulse bg-muted",
    avatar: "h-8 w-8 rounded-full animate-pulse bg-muted",
  },
  badge: {
    confirmed: "bg-sand/60 text-palm dark:bg-ink/40 dark:text-sand text-[12px] px-2 py-0.5",
    pending: "bg-sun/15 text-ink dark:bg-ink/40 dark:text-sun text-[12px] px-2 py-0.5",
    pendingPayment: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 text-[12px] px-2 py-0.5",
    paidAwaitingApproval: "bg-sand/60 text-ink-soft dark:bg-ink/40 dark:text-sand text-[12px] px-2 py-0.5",
    depositPaid: "bg-sand/60 text-ink-soft dark:bg-ink/40 dark:text-sand text-[12px] px-2 py-0.5",
    canceled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 text-[12px] px-2 py-0.5",
    canceledRefunded: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 text-[12px] px-2 py-0.5",
    declined: "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300 text-[12px] px-2 py-0.5",
    disputed: "bg-driftwood/20 text-ink-soft dark:bg-ink/40 dark:text-sand text-[12px] px-2 py-0.5",
    default: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 text-[12px] px-2 py-0.5",
  },
  bronze: {
    text: "text-ink",
    bg: "bg-ink",
    border: "border-sand-deep",
    hover: "hover:bg-sand/50",
    accent: BRONZE_600,
  },
  typography: {
    pageTitle: "text-2xl sm:text-3xl font-serif font-semibold tracking-tight text-foreground",
    sectionTitle: "text-lg sm:text-xl font-serif font-semibold tracking-tight text-foreground",
    subsectionTitle: "text-base font-serif font-semibold tracking-tight text-foreground",
    cardTitle: "text-[13px] font-sans font-medium tracking-tight text-muted-foreground",
    cardValue: "text-2xl sm:text-3xl font-serif font-semibold tracking-tight text-foreground",
    body: "text-sm font-sans font-normal tracking-tight text-foreground leading-relaxed",
    bodySmall: "text-[13px] font-sans font-normal tracking-tight text-foreground leading-relaxed",
    caption: "text-[12px] font-sans font-normal tracking-tight text-muted-foreground",
    // Deliberately smaller than sectionHeader below: a nav item is a child of
    // its section, and it was reading as the louder of the two.
    menuItem: "text-[13px] font-sans font-normal tracking-tight",
    menuItemActive: "text-[13px] font-sans font-semibold tracking-tight",
    menuSectionHeader: "text-[15px] font-sans font-medium tracking-tight",
    button: "text-[13px] font-sans font-medium tracking-tight",
    buttonSmall: "text-[12px] font-sans font-medium tracking-tight",
    tableHeader: "text-[12px] font-sans font-semibold tracking-wide uppercase text-muted-foreground",
    tableCell: "text-[13px] font-sans font-normal tracking-tight text-foreground",
    badge: "text-[12px] font-sans font-medium tracking-tight",
    label: "text-[13px] font-sans font-medium tracking-tight text-foreground",
    helperText: "text-[12px] font-sans font-normal tracking-tight text-muted-foreground",
    error: "text-[12px] font-sans font-normal tracking-tight text-red-600 dark:text-red-400",
    success: "text-[12px] font-sans font-normal tracking-tight text-palm dark:text-sand",
    warning: "text-[12px] font-sans font-normal tracking-tight text-sun-hover dark:text-sun",
    emptyState: "text-sm font-sans font-normal tracking-tight text-muted-foreground text-center",
    link: "text-[13px] font-sans font-normal tracking-tight text-ink hover:text-ink/70 underline-offset-2",
    listItem: "text-[13px] font-sans font-normal tracking-tight text-foreground",
    footer: "text-[12px] font-sans font-normal tracking-tight text-muted-foreground",
  },
  form: {
    label: "text-[13px] font-sans font-medium tracking-tight text-foreground mb-1",
    input: "text-[13px] font-sans font-normal tracking-tight",
    helper: "text-[12px] font-sans font-normal tracking-tight text-muted-foreground mt-1",
    error: "text-[12px] font-sans font-normal tracking-tight text-red-600 dark:text-red-400 mt-1",
  },
} as const;

export const getStatusBadgeClass = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'confirmed':
    case 'active':
    case 'success':
      return ADMIN_STYLES.badge.confirmed;
    case 'pending':
    case 'processing':
      return ADMIN_STYLES.badge.pending;
    case 'canceled':
    case 'cancelled':
    case 'failed':
    case 'error':
      return ADMIN_STYLES.badge.canceled;
    case 'declined':
      return ADMIN_STYLES.badge.declined;
    default:
      return ADMIN_STYLES.badge.default;
  }
};

export interface BookingStatusInfo {
  label: string;
  badgeClass: string;
  colorClass: string;
}

export const getBookingStatusDisplay = (
  status: string,
  paymentStatus?: string
): BookingStatusInfo => {
  const normalizedStatus = status?.toLowerCase() || '';
  const normalizedPayment = paymentStatus?.toLowerCase() || 'unpaid';

  if (normalizedStatus === 'confirmed') {
    return {
      label: 'Confirmed',
      badgeClass: ADMIN_STYLES.badge.confirmed,
      colorClass: 'bg-sand/60 border-sand-deep'
    };
  }

  if (normalizedStatus === 'canceled' || normalizedStatus === 'cancelled') {
    if (normalizedPayment === 'refunded') {
      return {
        label: 'Canceled - Refunded',
        badgeClass: ADMIN_STYLES.badge.canceledRefunded,
        colorClass: 'bg-gray-50 border-gray-200'
      };
    }
    return {
      label: 'Canceled',
      badgeClass: ADMIN_STYLES.badge.canceled,
      colorClass: 'bg-red-50 border-red-200'
    };
  }

  if (normalizedStatus === 'declined') {
    return {
      label: 'Declined',
      badgeClass: ADMIN_STYLES.badge.declined,
      colorClass: 'bg-red-50 border-red-100'
    };
  }

  if (normalizedStatus === 'pending') {
    if (normalizedPayment === 'paid') {
      return {
        label: 'Paid - Awaiting Approval',
        badgeClass: ADMIN_STYLES.badge.paidAwaitingApproval,
        colorClass: 'bg-sand/60 border-sand-deep'
      };
    }
    if (normalizedPayment === 'deposit_paid') {
      return {
        label: 'Deposit Paid',
        badgeClass: ADMIN_STYLES.badge.depositPaid,
        colorClass: 'bg-sand/60 border-sand-deep'
      };
    }
    if (normalizedPayment === 'disputed') {
      return {
        label: 'Payment Disputed',
        badgeClass: ADMIN_STYLES.badge.disputed,
        colorClass: 'bg-driftwood/20 border-sand-deep'
      };
    }
    return {
      label: 'Pending Payment',
      badgeClass: ADMIN_STYLES.badge.pendingPayment,
      colorClass: 'bg-orange-50 border-orange-200'
    };
  }

  return {
    label: status || 'Unknown',
    badgeClass: ADMIN_STYLES.badge.default,
    colorClass: 'bg-gray-50 border-gray-200'
  };
};

export const AdminSkeleton = {
  Card: () => (
    `<div class="${ADMIN_STYLES.skeleton.card}" />`
  ),
  StatsGrid: (count = 4) => 
    Array.from({ length: count }).map((_, i) => ({
      key: i,
      className: ADMIN_STYLES.skeleton.card,
    })),
};
